import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyButton } from "../../components/CopyButton";
import { Pagination } from "../../components/Pagination";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Input,
  LoadingBlock,
  Mono,
  Select,
} from "../../components/ui";
import {
  EMPTY_MODEL_FILTER,
  MODELS_PER_PAGE,
  MODEL_SORTS,
  capabilitiesOf,
  stockedCategoriesOf,
  categoryLabel,
  categoryOf,
  filterModels,
  formatTokens,
  isCostPriced,
  isFreeModel,
  isNewModel,
  modelFilterIsEmpty,
  paginate,
  partitionAiModels,
  pricePanel,
  publishersOf,
  sortModels,
  type ModelFilter,
  type ModelSortKey,
} from "../../lib/inference";
import { useAiModels } from "../../lib/queries";
import type { AiModel } from "../../lib/types";
import { CategoryFacet, Chip, PriceBlock, RailSection, Tag } from "./parts";

/** How many publisher chips the row shows before collapsing the rest. */
const PUBLISHER_CHIP_LIMIT = 8;

/**
 * The catalogue, browsable.
 *
 * It used to be a table of everything in featured-then-newest order, which is the
 * right default and the only order available. That works at three models and
 * stops working at thirty — and thirty is now one sync away, since the daily scan
 * discovers models an operator can list without a deploy.
 *
 * So: search, filter, sort. The pieces are chosen from what somebody actually
 * arrives asking, in roughly this frequency — "is there a cheap model for this",
 * "does anything here do embeddings", "what is the biggest context I can get",
 * "what is this id I found in a config". The last one is why search covers the id
 * as well as the name.
 *
 * Featured models stay pinned at the top *only* in the default order. Under an
 * explicit sort they take their place in it: somebody who asked for cheapest
 * first and got an expensive featured model at the top would reasonably conclude
 * the sort is broken.
 */
export function ModelsTab() {
  const { t } = useTranslation("ai");
  const models = useAiModels();
  const [filter, setFilterState] = useState<ModelFilter>(EMPTY_MODEL_FILTER);
  const [sort, setSortState] = useState<ModelSortKey>("newest");
  const [page, setPage] = useState(1);
  const [showAllPublishers, setShowAllPublishers] = useState(false);

  /*
   * Narrowing or reordering the list returns to the first page, and it is wired
   * here rather than left to the eight call sites that change a filter. Filter
   * down to three models while on page four and the table is empty for a reason
   * nothing on screen explains: a pager, no rows, and a count above it claiming
   * three matches. Wrapping the setters is what makes that unforgettable — there
   * is no other way to change either one.
   */
  const setFilter = (next: ModelFilter) => {
    setFilterState(next);
    setPage(1);
  };
  const setSort = (next: ModelSortKey) => {
    setSortState(next);
    setPage(1);
  };

  const listed = models.data ?? [];
  const publishers = useMemo(() => publishersOf(listed), [listed]);
  const capabilities = useMemo(() => capabilitiesOf(listed), [listed]);
  /*
   * Counted against everything except the category, so each tab says what it
   * would give if it were the one open. Counting against the full filter would
   * put a zero on every tab but the current one; counting against nothing would
   * offer shelves the chosen publisher does not stock.
   */
  const acrossCategories = useMemo(
    () => filterModels(listed, { ...filter, category: null }),
    [listed, filter],
  );
  const categories = useMemo(
    () => stockedCategoriesOf(acrossCategories),
    [acrossCategories],
  );
  /*
   * The same trick one facet down: counted against every filter except this one,
   * so each count answers "given what I have already narrowed to, how many are
   * priced that way" rather than reading zero on every option but the open one.
   */
  const acrossPricing = useMemo(
    () => filterModels(listed, { ...filter, pricing: null }),
    [listed, filter],
  );

  const visible = useMemo(() => filterModels(listed, filter), [listed, filter]);

  /*
   * Two orders, and which one applies depends on whether the reader asked for
   * one. `newest` is the default, and in it the featured ranking leads — that
   * ranking is an operator's explicit "start here", which is exactly what a
   * default order is for.
   *
   * Featured is also the part that does not paginate. It is a handful of models
   * an operator has pointed at deliberately, and putting that behind a pager
   * would hide the answer to make room for the list.
   */
  const { featured, rest } = useMemo(() => {
    if (sort !== "newest") return { featured: [], rest: sortModels(visible, sort) };
    const { featured, regular } = partitionAiModels(visible);
    return { featured, rest: regular };
  }, [visible, sort]);

  /*
   * `paginate` clamps, which covers the case the setters above cannot: a refetch
   * that returns fewer models than the last render. There is no event to reset on
   * there — the list simply changes under a page number that was valid a moment
   * ago — so the clamped page is what gets rendered and handed to the pager, and
   * the state catches up on the next interaction.
   */
  const shown = paginate(rest.length, page);
  const groups = [
    { label: t("models.featured"), models: featured },
    {
      label: featured.length > 0 ? t("models.allModels") : null,
      models: rest.slice(shown.from, shown.to),
    },
  ].filter((group) => group.models.length > 0);

  /* Publishers and capabilities only. Category stopped going through here when it
     became exclusive — there is nothing to toggle in a choice of one. */
  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

  if (models.isLoading) return <LoadingBlock />;
  if (models.isError) {
    return <Alert>{t("models.loadFailed")}</Alert>;
  }
  if (listed.length === 0) {
    return (
      <Card>
        <EmptyState
          icon="models"
          title={t("models.empty")}
          description={t("models.emptyDescription")}
        />
      </Card>
    );
  }

  const visiblePublishers = publishers.filter(
    (publisher, index) =>
      showAllPublishers || index < PUBLISHER_CHIP_LIMIT || filter.publishers.includes(publisher),
  );

  return (
    /*
     * A rail and a list, which is what a catalogue of four hundred models is.
     *
     * Every control used to sit in a card stacked above the table — search and
     * sort, then the category row, then Publisher, Can and two loose toggles
     * crammed into one wrapping line. Three things were wrong with it. The card
     * grew taller as the catalogue grew wider, so on a laptop the reader saw a
     * screenful of controls and two models. The filters scrolled away the moment
     * anybody started reading, so changing one meant scrolling back. And that last
     * wrapping line put a labelled group and an unlabelled chip on the same
     * footing, which at a narrow width was simply a jumble.
     *
     * A sticky rail fixes all three at once, and it is what the public marketplace
     * has always used over the same catalogue. Below `lg` it stacks above the
     * list, where sticky would cost more room than it earns.
     */
    <div className="grid items-start gap-4 lg:grid-cols-[15rem_minmax(0,1fr)]">
      <Card className="lg:sticky lg:top-4">
        <RailSection>
          <Input
            type="search"
            value={filter.query}
            onChange={(event) => setFilter({ ...filter, query: event.target.value })}
            placeholder={t("models.search")}
            aria-label={t("models.searchLabel")}
          />
        </RailSection>

        {/*
          Category leads the rail, because it is the first question somebody
          arrives with — text, image, embeddings — and it replaced a three-way
          "Task" control that could only answer chat, embedding or all. "Chat" was
          doing duty for every model that speaks the chat protocol, which includes
          ones that return images.

          Exclusive, and shaped as a list rather than as chips, because a category
          is a choice of shelf and not a condition that combines with the ones
          below it. All eight are here, including the ones this deployment has
          nothing in: what a deployment has listed is an operator's choice, so
          several are empty on most catalogues, and "Video 0" states what we carry
          where a missing row is silence.
        */}
        <RailSection title={t("models.category")}>
          <div role="group" aria-label={t("models.category")} className="-mx-1">
            <CategoryFacet
              label={t("models.allModels")}
              count={acrossCategories.length}
              selected={filter.category === null}
              onClick={() => setFilter({ ...filter, category: null })}
            />
            {categories.map((category) => (
              <CategoryFacet
                key={category.key}
                label={categoryLabel(category.key, t)}
                count={category.count}
                selected={filter.category === category.key}
                onClick={() => setFilter({ ...filter, category: category.key })}
              />
            ))}
          </div>
        </RailSection>

        {/*
          Capped, because this group is unbounded by nature: a sync through the
          whole supplier catalogue can leave forty publishers, and forty chips in a
          rail is a column nobody scrolls past.

          A selected publisher is always shown even when it falls outside the cap.
          Collapsing must never hide a filter that is currently narrowing the list,
          which would leave a short table with nothing on screen explaining why.
        */}
        <RailSection title={t("models.publisher")}>
          <div className="flex flex-wrap gap-1.5">
            {visiblePublishers.map((publisher) => (
              <Chip
                key={publisher}
                selected={filter.publishers.includes(publisher)}
                onClick={() =>
                  setFilter({ ...filter, publishers: toggle(filter.publishers, publisher) })
                }
              >
                {publisher}
              </Chip>
            ))}
          </div>
          {publishers.length > PUBLISHER_CHIP_LIMIT && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 -ml-2"
              onClick={() => setShowAllPublishers(!showAllPublishers)}
            >
              {showAllPublishers
                ? t("models.showFewer")
                : t("models.more", { count: publishers.length - PUBLISHER_CHIP_LIMIT })}
            </Button>
          )}
        </RailSection>

        {/*
          Publishers OR, capabilities AND. Not an inconsistency — two publishers
          ticked means "either vendor", while two capabilities ticked means "must
          do both", because a model that streams but has no tool use is no use to
          somebody who needs tool use.
        */}
        {capabilities.length > 0 && (
          <RailSection title={t("models.can")}>
            <div className="flex flex-wrap gap-1.5">
              {capabilities.map((capability) => (
                <Chip
                  key={capability}
                  selected={filter.capabilities.includes(capability)}
                  onClick={() =>
                    setFilter({
                      ...filter,
                      capabilities: toggle(filter.capabilities, capability),
                    })
                  }
                >
                  {/* The key is kebab-case because it is a filter value; the
                      chip reads it in words. */}
                  {capability.replace(/-/g, " ")}
                </Chip>
              ))}
            </div>
          </RailSection>
        )}

        {/*
          The question the two pricing bases created, and the rail had no way to
          ask it.

          A rate card is a promise: you can work out what a run will cost before
          you send anything. A cost-priced model cannot make that promise — the
          charge is known after the call. Somebody budgeting cares about that
          difference more than about any single rate on this page, and the only
          way to ask used to be reading every row.

          Exclusive, and shaped like the category above it for the same reason:
          these are two answers to one question rather than two conditions that
          combine. Offered only when the catalogue holds both, since a filter
          that cannot change the list teaches the reader to distrust the rail.
        */}
        {listed.some(isCostPriced) && listed.some((model) => !isCostPriced(model)) && (
          <RailSection title={t("models.pricing")}>
            <div role="group" aria-label={t("models.pricing")} className="-mx-1">
              <CategoryFacet
                label={t("models.any")}
                count={acrossPricing.length}
                selected={filter.pricing === null}
                onClick={() => setFilter({ ...filter, pricing: null })}
              />
              <CategoryFacet
                label={t("models.fixed")}
                count={acrossPricing.filter((model) => !isCostPriced(model)).length}
                selected={filter.pricing === "fixed"}
                onClick={() => setFilter({ ...filter, pricing: "fixed" })}
              />
              <CategoryFacet
                label={t("models.perRequest")}
                count={acrossPricing.filter(isCostPriced).length}
                selected={filter.pricing === "request"}
                onClick={() => setFilter({ ...filter, pricing: "request" })}
              />
            </div>
          </RailSection>
        )}

        {/*
          The one loose chip that used to trail the filter line, now under a
          title that says what it is. Zero data retention used to sit beside it,
          and is gone: a model's retention posture is stated on its row, where
          somebody who needs it reads it beside the model rather than narrowing
          the whole catalogue to one shelf. Free is offered only when the
          catalogue holds one: a chip that always returns nothing teaches the
          reader to distrust the rest of the rail.
        */}
        {listed.some(isFreeModel) && (
          <RailSection title={t("models.onlyShow")}>
            <div className="flex flex-wrap gap-1.5">
              <Chip
                selected={filter.freeOnly}
                onClick={() => setFilter({ ...filter, freeOnly: !filter.freeOnly })}
              >
                {t("models.free")}
              </Chip>
            </div>
          </RailSection>
        )}

        {!modelFilterIsEmpty(filter) && (
          <RailSection>
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2"
              onClick={() => setFilter(EMPTY_MODEL_FILTER)}
            >
              {t("models.clearFilters")}
            </Button>
          </RailSection>
        )}
      </Card>

      <div className="min-w-0 space-y-4">
        {/*
          The count and the order, on one line above the list.

          Sort lives here rather than in the rail because it is not a filter: it
          changes how the answer is arranged, not what the answer is, and reading
          it beside the count of what is being arranged is the whole of it.
        */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[13px] text-fg-muted" aria-live="polite">
            {visible.length === listed.length
              ? t("models.count", { count: listed.length })
              : t("models.ofTotal", { shown: visible.length, total: listed.length })}
          </p>
          <Select
            value={sort}
            onChange={(event) => setSort(event.target.value as ModelSortKey)}
            aria-label={t("models.sortBy")}
            className="w-auto shrink-0"
          >
            {MODEL_SORTS.map((option) => (
              <option key={option.key} value={option.key}>
                {t(`models.sort.${option.key}` as "models.sort.newest")}
              </option>
            ))}
          </Select>
        </div>

        {visible.length === 0 ? (
          <Card>
            <EmptyState
              icon="models"
              title={t("models.noMatch")}
              description={t("models.noMatchDescription")}
              action={
                <Button variant="secondary" size="sm" onClick={() => setFilter(EMPTY_MODEL_FILTER)}>
                  {t("models.clearFilters")}
                </Button>
              }
            />
          </Card>
        ) : (
          <Card>
            {/*
              No table head, because there is no longer a set of columns every
              row can fill. Each row states its own units beside its own figures
              — see `PriceBlock` — which is what let the header, the spanned
              cell, the basis tag and the paragraph that explained all three come
              out together.
            */}
            {groups.map((group) => (
              <ModelGroup key={group.label ?? "all"} label={group.label} models={group.models} />
            ))}

            {/*
              The range counts the paged list, not the catalogue — featured sits
              above it unpaged, so "1–25 of 340" beside a header reading "342
              models" is not a contradiction. It reads correctly because the pager
              sits directly under the section the "All models" label opens.
            */}
            <Pagination
              total={shown.total}
              perPage={MODELS_PER_PAGE}
              page={shown.page}
              onChange={setPage}
            />
          </Card>
        )}
      </div>
    </div>
  );
}

/**
 * A named run of rows, hairline-separated.
 *
 * The heading is a band rather than a table row now, and it keeps the job it
 * had: "Featured" is an operator's short start-here list and "All models" is
 * everything after it, and without a label between them the two read as one
 * order that inexplicably restarts.
 */
function ModelGroup({ label, models }: { label: string | null; models: AiModel[] }) {
  return (
    <>
      {label && (
        <p className="micro border-b border-line bg-raised px-4 py-2">{label}</p>
      )}
      <ul className="divide-y divide-line">
        {models.map((model) => (
          <ModelRow key={model.id} model={model} />
        ))}
      </ul>
    </>
  );
}

/**
 * One model, as a row rather than a table row.
 *
 * The table is gone and this is why. Its two rate columns described one of the
 * four ways a model can be priced: an embedding model put a meaningless zero in
 * Output, a generation model spanned both with figures that are not per-million
 * anything, and an unpriced one showed two dashes. A column whose meaning
 * changes per row is not a column, and the patches that kept it working — a
 * spanned cell, a tag in the capability strip, a paragraph underneath — were
 * three separate explanations of one thing.
 *
 * A row with a price block on the right holds every basis without changing
 * shape, and it is what the public marketplace has always used over the same
 * catalogue. Somebody who reads both should not have to learn two layouts.
 */
function ModelRow({ model }: { model: AiModel }) {
  const { t } = useTranslation("ai");
  const panel = pricePanel(model, t);

  return (
    <li className="grid grid-cols-1 gap-x-6 gap-y-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">
        {/*
          Featured moved in here from a column of its own. That column was a
          full-width strip carrying a badge on perhaps three rows in four hundred,
          and the rail took the width it was wasting. Beside the name is also
          where the badge means something: it qualifies this model, not this row.
        */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="font-medium text-fg">{model.name}</span>
          <span className="text-[11px] text-fg-subtle">{model.owned_by}</span>
          {model.featured_rank !== null && <Tag>{t("models.featuredTag")}</Tag>}
          {/* Beside the name, with featured, because both qualify the model
              rather than the row. `accent` is the palette's attention colour
              that is not a state — mint, warn and danger each already mean
              something about health. */}
          {isNewModel(model) && (
            <span className="border border-accent/40 bg-accent-soft px-1.5 py-0.5 text-[10px] tracking-[0.08em] text-accent uppercase">
              {t("models.new")}
            </span>
          )}
        </div>
        {/*
          The id sits directly under the name with its own copy button, because
          copying it is the actual task this page exists for — every tool that
          consumes this endpoint asks for the id verbatim, and selecting a mono
          string out of a table by hand is where a trailing space comes from.
        */}
        <div className="mt-1 flex items-center gap-1.5">
          <Mono className="truncate text-xs text-fg-subtle">{model.id}</Mono>
          <CopyButton value={model.id} label={t("models.copyId", { name: model.name })} />
        </div>
        {/*
          Clamped, which it was not. Supplier descriptions run from one line to
          fifteen, and an unclamped one turned a single row into most of a screen
          — so the page a reader is scanning had a height that depended on which
          models happened to land on it. Two lines is enough to tell two models
          apart, which is all this is for.
        */}
        {model.description && (
          <p className="mt-1.5 line-clamp-2 max-w-prose text-xs leading-5 text-fg-subtle">
            {model.description}
          </p>
        )}
        {/*
          Capability only. The pricing basis used to sit in this strip — the one
          tag here that described the price rather than the model — and it has
          moved to the price block, beside the figures it qualifies. Context
          joined the strip from a column of its own: it is a property of the
          model like everything else here, and as a column it was `priority="md"`
          and therefore absent from the narrow screens where a 1M window is worth
          most.
        */}
        <div className="mt-2 flex flex-wrap gap-1">
          <Tag>{categoryLabel(categoryOf(model), t)}</Tag>
          {model.context_window !== null && (
            <Tag>{t("models.ctx", { tokens: formatTokens(model.context_window) })}</Tag>
          )}
          {model.zero_data_retention && <Tag>{t("models.noRetention")}</Tag>}
          {model.capabilities.map((capability) => (
            <Tag key={capability}>{capability}</Tag>
          ))}
        </div>
      </div>

      <PriceBlock panel={panel} />
    </li>
  );
}
